import {
    actions,
    ActionTypes,
    ActorRefFrom,
    createMachine,
    DoneInvokeEvent,
    EventObject,
    InterpreterFrom,
    LogAction,
    sendParent,
    StateMachine,
} from "xstate"
import * as Schema from "effect/Schema"
import * as ParseResult from "effect/ParseResult"
import * as E from "effect/Either"
import * as ServerNotification from "@ssc-booking/api-contracts"
import { PromiseController } from "../../api/promise-controller"
import { assertEvent } from "../../xstate-helpers"

export type ServerNotificationChannel = {
    receiveServerNotification(): Promise<Record<string, unknown> | undefined>
}

export type InitialServerNotificationContext = {}

export type ServerNotificationTypeState =
    | { value: "listening"; context: InitialServerNotificationContext }
    | { value: "error"; context: InitialServerNotificationContext }
    | { value: "stopped"; context: InitialServerNotificationContext }

export type ServerNotificationContext = InitialServerNotificationContext

export type NotificationReceived<ASuccess> = { type: "NOTIFICATION_RECEIVED"; response: ASuccess }
export type NotificationError = { type: "NOTIFICATION_ERROR"; message: string; data: unknown }
export type ServerNotificationEvents<ASuccess> =
    | NotificationReceived<ASuccess>
    | NotificationError
    | (DoneInvokeEvent<unknown> & { type: ActionTypes.DoneInvoke })

export type ServerNotificationStateMachine<ASuccess> = StateMachine<
    ServerNotificationContext,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    ServerNotificationEvents<ASuccess>,
    ServerNotificationTypeState
>

type ServerNotificationInterpreter<ASuccess> = InterpreterFrom<ServerNotificationStateMachine<ASuccess>>
type ServerNotificationState<ASuccess> = ServerNotificationInterpreter<ASuccess>["state"]
type ServerNotificationSend<ASuccess> = ServerNotificationInterpreter<ASuccess>["send"]
type ActorRef<ASuccess> = ActorRefFrom<ServerNotificationStateMachine<ASuccess>>
type ServerNotificationTypes<ASuccess> = {
    Context: ServerNotificationContext
    Events: ServerNotificationEvents<ASuccess>
    NotificationReceived: NotificationReceived<ASuccess>
    NotificationError: NotificationError
    TypeState: ServerNotificationTypeState
    StateMachine: ServerNotificationStateMachine<ASuccess>
    Interpreter: ServerNotificationInterpreter<ASuccess>
    State: ServerNotificationState<ASuccess>
    Send: ServerNotificationSend<ASuccess>
    ActorRef: ActorRef<ASuccess>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyNotification = Schema.Schema<any, any, never>

export type ServerNotificationConfig<Notification extends AnyNotification, TParentEvent extends EventObject> = {
    channel: ServerNotificationChannel
    knownNotifications: Record<string, Notification>
    notifyParent: (ev: NotificationReceived<Notification["Type"]> | NotificationError) => TParentEvent | undefined
    disableLog?: boolean
}

const decodeServerNotification = Schema.decodeUnknownEither(ServerNotification.ServerNotification)

export const serverNotificationMachine = <Notification extends AnyNotification, TParentEvent extends EventObject>({
    channel,
    knownNotifications,
    notifyParent,
    disableLog = true,
}: ServerNotificationConfig<Notification, TParentEvent>) => {
    type Events = ServerNotificationTypes<Notification>["Events"]
    type Context = ServerNotificationTypes<Notification>["Context"]
    type TypeState = ServerNotificationTypes<Notification>["TypeState"]

    const logIfEnabled = (logAction: LogAction<Context, Events>) =>
        actions.pure<Context, Events>(() => (disableLog ? undefined : logAction))

    return createMachine<Context, Events, TypeState>(
        {
            id: "server-notification-machine",
            initial: "listening",
            predictableActionArguments: true,
            context: {},
            states: {
                listening: {
                    invoke: {
                        src: (_ctx, _ev) => async (sendBack, _onReceive) => {
                            let keepRunning = true
                            const promiseController = new PromiseController<null>()
                            while (keepRunning) {
                                const message = await Promise.race([
                                    channel.receiveServerNotification(),
                                    promiseController.wait(),
                                ])
                                if (!message) continue

                                const maybeEvent = serverNotificationHandler(message, knownNotifications)
                                if (maybeEvent) sendBack(maybeEvent)
                            }

                            return () => {
                                keepRunning = false
                                promiseController.resolve(null)
                            }
                        },
                        onDone: {
                            target: "stopped",
                            actions: ["logCompleted"],
                        },
                        onError: {
                            target: "error",
                            actions: ["logFatalError"],
                        },
                    },
                },
                error: {},
                stopped: {},
            },
            on: {
                NOTIFICATION_RECEIVED: { actions: ["notifyParent"] },
                NOTIFICATION_ERROR: { actions: ["notifyParent"] },
            },
        },
        {
            actions: {
                notifyParent: actions.pure<Context, Events>((_, ev) => {
                    assertEvent(ev, "NOTIFICATION_RECEIVED", "NOTIFICATION_ERROR")
                    const parentEvent = notifyParent(ev)
                    return parentEvent ? sendParent(parentEvent) : undefined
                }),
                logCompleted: logIfEnabled(
                    actions.log<Context, Events>(() => ({ message: "Server notification machine done" })),
                ),
                logFatalError: actions.pure<Context, Events>((_, ev) => {
                    if (!ev.type.startsWith(ActionTypes.ErrorPlatform)) return undefined
                    const event = ev as DoneInvokeEvent<unknown>
                    return [
                        actions.log(() => ({ message: "Server notification machine fatal error", error: event.data })),
                    ]
                }),
            },
        },
    )
}

export const serverNotificationConfig = <Notification extends AnyNotification, TParentEvent extends EventObject>(
    config: ServerNotificationConfig<Notification, TParentEvent>,
) => ({
    invoke: {
        id: "serverNotification",
        src: serverNotificationMachine(config),
    },
    on: {},
})

export const serverNotificationHandler = <Notification extends AnyNotification>(
    message: Record<string, unknown>,
    knownNotifications: Record<string, Notification>,
) => {
    const decodedSN = decodeServerNotification(message)
    if (E.isLeft(decodedSN)) return undefined
    const serverNotification = decodedSN.right

    const notification = knownNotifications[serverNotification.type]
    if (!notification) return undefined

    const response = Schema.decodeUnknownEither(notification)(serverNotification.data)

    return E.isRight(response)
        ? notificationReceived(response.right)
        : notificationError("Server notification parse error", {
              notificationType: serverNotification.type,
              error: safeFormatParseError(response.left),
          })
}

const notificationReceived = <T>(response: T): NotificationReceived<T> => ({ type: "NOTIFICATION_RECEIVED", response })
const notificationError = (message: string, data: unknown): NotificationError => ({
    type: "NOTIFICATION_ERROR",
    message,
    data,
})

const safeFormatParseError = (error: ParseResult.ParseError) => {
    try {
        return ParseResult.ArrayFormatter.formatErrorSync(error)
    } catch (e) {
        return ["Cannot serialize parse error", String(e)]
    }
}
